import { useState } from "react";
import { useParams } from "react-router-dom";
import { LifeBuoy, Hand, StickyNote, Reply, Share2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Info, History, ActionButton, RunButton, BackLink, useLoad, fmt, errText,
} from "@/components/control/kit";

function ReplyButton({ t, onDone }) {
  const templates = useLoad("/support/templates");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const first = (t.member_name || "").split(" ")[0];
  const pick = (key) => {
    const tpl = (templates.data?.templates || []).find((x) => x.key === key);
    if (tpl) setText(tpl.text.replace("{vorname}", first).replace("{aktivitaet}", "…"));
  };
  const send = async () => {
    if (!text.trim()) { setError("Bitte eine Antwort eingeben."); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/support/tickets/${t.id}/reply`, { text });
      toast.success("Antwort an das Mitglied gesendet.");
      setOpen(false);
      setText("");
      onDone();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button size="sm" variant="default" onClick={() => setOpen(true)} disabled={!t.member_id} data-testid="reply-ticket" className="gap-1.5 bg-brand-magenta hover:bg-brand-magenta-dark">
        <Reply className="h-4 w-4" /> Antworten
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reply-ticket-dialog" className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Antwort an {t.member_name}</DialogTitle>
            <DialogDescription>Die Antwort erscheint in der Mitglieder-App. Vorlagen können angepasst werden.</DialogDescription></DialogHeader>
          <label htmlFor="tpl" className="flex flex-col gap-1 text-sm font-medium">Antwortvorlage
            <select id="tpl" onChange={(e) => pick(e.target.value)} defaultValue="" data-testid="reply-template" className="h-10 rounded-lg border border-input px-3 text-sm">
              <option value="">Keine Vorlage</option>
              {(templates.data?.templates || []).map((x) => <option key={x.key} value={x.key}>{x.title}</option>)}
            </select>
          </label>
          <label htmlFor="reply-text" className="flex flex-col gap-1 text-sm font-medium">Antwort *
            <textarea id="reply-text" rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="reply-text" className="rounded-lg border border-input p-3 text-sm" />
          </label>
          {error && <p role="alert" className="text-sm text-red-700" data-testid="reply-error">{error}</p>}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={send} disabled={busy} data-testid="reply-send" className="bg-brand-magenta hover:bg-brand-magenta-dark">
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Actions({ d, reload }) {
  const t = d.ticket;
  const base = `/support/tickets/${t.id}`;
  const open = !["gelöst"].includes(t.status);
  return (
    <>
      <Status value={t.status} testId="ticket-status" />
      {["neu", "entwurf"].includes(t.status) && <RunButton label="Übernehmen" icon={Hand} testId="claim-ticket" success="Supportfall übernommen." onDone={reload} run={() => api.post(`${base}/claim`)} />}
      {open && <ReplyButton t={t} onDone={reload} />}
      {open && (
        <ActionButton label="Weitergeben" icon={Share2} testId="handover-ticket" onDone={reload} title="Supportfall weitergeben"
          page="weitergeben" pageSubtitle="Begründe die Weitergabe und begrenze den weitergegebenen Kontext." submitLabel="Weitergeben"
          description="Gib nur die Felder weiter, die für die Prüfung nötig sind."
          fields={[
            { name: "purpose", label: "Zweck der Weitergabe", type: "textarea", required: true },
            { name: "fields", label: "Weitergegebene Felder", type: "checks", required: true, options: Object.entries(d.fields).map(([value, label]) => ({ value, label })) },
            { name: "text", label: "Zusammenfassung", type: "textarea" },
          ]}
          onSubmit={async (v) => { await api.post(`${base}/handover`, v); return "An Moderation weitergegeben."; }} />
      )}
      {open && (
        <ActionButton label="Lösen" icon={CheckCircle2} testId="resolve-ticket" onDone={reload} title="Supportfall lösen" submitLabel="Als gelöst markieren"
          page="loesen" pageSubtitle="Prüfe Abschlussantwort und Lösung, bevor du den Supportfall abschliesst."
          fields={[
            { name: "category", label: "Lösungskategorie", type: "select", required: true,
              options: ["Erklärt", "Behoben", "Weitergegeben", "Keine Rückmeldung", "Kein Handlungsbedarf"].map((x) => ({ value: x, label: x })) },
            { name: "text", label: "Abschlussantwort", type: "textarea", required: true },
          ]}
          onSubmit={async (v) => { await api.post(`${base}/resolve`, v); return "Supportfall gelöst."; }} />
      )}
    </>
  );
}

function TicketView({ d, reload }) {
  const t = d.ticket;
  return (
    <>
      <PageHeader title={`${t.number} · ${t.subject}`} subtitle={`${t.area} · erfasst ${fmt(t.created_at)}`} icon={LifeBuoy} actions={<Actions d={d} reload={reload} />} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Anliegen" testId="ticket-info">
          <Info items={[["Mitglied", t.member_name], ["Dringlichkeit", <Status value={t.urgency} />], ["Zuständig", t.assigned_to?.name],
            ["Nachricht", t.message], t.attachment_note && ["Anhänge", t.attachment_note],
            t.resolution_category && ["Lösung", t.resolution_category], t.handover_report_id && ["Weitergabe", "an Meldungen & Sicherheit"]]} />
        </Section>
        <Section title="Interne Notizen" testId="ticket-notes" actions={
          <ActionButton label="Notiz" icon={StickyNote} testId="add-ticket-note" onDone={reload} submitLabel="Notiz speichern"
            page="notiz" title="Interne Supportnotiz" pageSubtitle="Die interne Notiz ist keine Antwort an das Mitglied."
            description="Nur für das zuständige Support-Team sichtbar."
            fields={[{ name: "text", label: "Notiz", type: "textarea", required: true }]}
            onSubmit={async (v) => { await api.post(`/support/tickets/${t.id}/note`, v); return "Notiz gespeichert."; }} />
        }>
          {(t.notes || []).length === 0 ? <Empty text="Noch keine Notizen." /> : (
            <ul className="space-y-2">{t.notes.map((n, i) => <li key={i} className="rounded-lg bg-secondary/60 p-3 text-sm">{n.text}<p className="mt-1 text-xs text-muted-foreground">{n.by} · {fmt(n.at)}</p></li>)}</ul>
          )}
        </Section>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Section title="Antworten an das Mitglied" testId="ticket-replies">
          {(t.replies || []).length === 0 ? <Empty text="Noch keine Antworten." /> : (
            <ul className="space-y-2">{t.replies.map((r, i) => <li key={i} className="rounded-lg border border-border p-3 text-sm">{r.text}<p className="mt-1 text-xs text-muted-foreground">{r.by} · {fmt(r.at)}</p></li>)}</ul>
          )}
        </Section>
        <Section title="Verlauf" testId="ticket-history"><History items={t.history} /></Section>
      </div>
    </>
  );
}

export default function SupportDetail() {
  const { id } = useParams();
  const st = useLoad(`/support/tickets/${id}`);
  return (
    <div>
      <BackLink to="/support">Zurück zu Support</BackLink>
      <Loadable state={st}>{(d) => <TicketView d={d} reload={st.reload} />}</Loadable>
    </div>
  );
}
